import { EditorContent } from '@tiptap/react';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import '../../../pages/AdminSupportInboxPage/styles/composerEditor.css';
import { ISupportTicket } from '../../../../models/Support/ISupportTicket';
import { Channel } from '../../../../models/Support/SupportEnums';
import { isValidEmail } from '../../../../models/utils/validationUtils';
import { useCreateTicketMutation } from '../../../../queries/Support/useSupportTicketMutations';
import { getApiErrorMessage } from '../../../../utils/Support/supportHelpers';
import { ComposerAttachmentList } from '../../../pages/AdminSupportInboxPage/TicketComposer/ComposerAttachmentList';
import { useComposerAttachments } from '../../../pages/AdminSupportInboxPage/TicketComposer/useComposerAttachments';
import { useComposerInlineImages } from '../../../pages/AdminSupportInboxPage/TicketComposer/useComposerInlineImages';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import {
	Dialog,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { Input } from '../../../../web/components-v2/Input';
import { Label } from '../../../../web/components-v2/Label';
import { buildAdminSupportTicketUrl } from '../../../utils';
import { supportSharedStyles as shared } from '../styles/supportSharedStyles';
import type { NewTicketDraftAccount } from './newTicketDraft';
import { NewTicketFields } from './NewTicketFields';
import { newTicketStyles as s } from './styles';
import { useDescriptionEditor } from './useDescriptionEditor';
import { useNewTicketDraftSource, useNewTicketFormState } from './useNewTicketFormState';

export interface InternalTicketModalProps {
	/** Prefills the account (and its CSM) — set when launched from the account details page or an account-scoped inbox. */
	defaultAccount?: NewTicketDraftAccount;
	defaultMailboxId?: string;
	onClose: () => void;
	/** When provided the host owns post-create navigation; otherwise the success toast links to the ticket. */
	onCreated?: (ticket: ISupportTicket) => void;
}

/** The description is recorded as the customer's request, not as an agent note. */
export const InternalTicketModal: React.FC<InternalTicketModalProps> = ({
	defaultAccount,
	defaultMailboxId,
	onClose,
	onCreated,
}) => {
	const draftSource = useNewTicketDraftSource('internal', defaultAccount?.id);
	const initialDescriptionHtml = draftSource.initialDraft?.descriptionHtml ?? '';

	const [descriptionHtml, setDescriptionHtml] = React.useState(initialDescriptionHtml);
	const [channel, setChannel] = React.useState<Channel>(draftSource.initialDraft?.channel ?? Channel.Email);
	const [error, setError] = React.useState('');
	const [noContactConfirmed, setNoContactConfirmed] = React.useState(false);

	const form = useNewTicketFormState({
		defaultAccount,
		defaultMailboxId,
		draftSource,
		descriptionHtml,
		channel,
	});

	const createTicket = useCreateTicketMutation();
	const attachments = useComposerAttachments();

	// insertDescriptionFiles is declared below (it needs the editor); the callbacks only run on
	// paste/drop, long after render, so the binding is initialized by then.
	const editor = useDescriptionEditor(
		{
			insertFiles: (files, insertAt) => insertDescriptionFiles(files, insertAt),
			onUpdate: instance => setDescriptionHtml(instance.isEmpty ? '' : instance.getHTML()),
		},
		initialDescriptionHtml
	);
	const inlineImages = useComposerInlineImages(editor);
	const hasUploading = attachments.hasUploading || inlineImages.hasUploadingImages;

	// Same split as the ticket composer: images land inline in the description, other files attach.
	const insertDescriptionFiles = (files: File[], insertAt?: number) => {
		const imageFiles = files.filter(file => file.type.startsWith('image/'));
		const otherFiles = files.filter(file => !file.type.startsWith('image/'));
		if (imageFiles.length > 0) inlineImages.insertImages(imageFiles, insertAt);
		if (otherFiles.length > 0) attachments.addFiles(otherFiles);
	};

	// Pasting anywhere in the form takes clipboard files, so a screenshot can be dropped in without
	// focusing the description first. The editor consumes its own paste events (defaultPrevented),
	// so this never double-adds; text pastes pass through untouched.
	const handlePaste = (event: React.ClipboardEvent) => {
		if (event.defaultPrevented) return;
		const files = Array.from(event.clipboardData?.files ?? []);
		if (files.length === 0) return;
		event.preventDefault();
		insertDescriptionFiles(files);
	};

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (!files || files.length === 0) return;
		insertDescriptionFiles(Array.from(files));
		event.target.value = '';
	};

	const isMissingContact = !!form.account && (form.manualContact ? !(form.contact?.email ?? '').trim() : !form.contact);

	const handleSubmit = () => {
		const trimmedSubject = form.subject.trim();
		const manualEmail = form.manualContact ? (form.contact?.email ?? '').trim() : '';
		// Both slots reset up front: an early return otherwise leaves the previous submit's message
		// showing next to the new one.
		setError('');
		form.setContactError('');
		if (!form.mailboxId) {
			setError('Select a mailbox for the ticket.');
			return;
		}
		if (!trimmedSubject) {
			setError('Subject is required.');
			return;
		}
		if (!descriptionHtml.trim()) {
			setError('Description is required.');
			return;
		}
		if (form.manualContact && manualEmail && !isValidEmail(manualEmail)) {
			form.setContactError('Please enter a valid email address');
			return;
		}
		if (hasUploading) {
			setError('Attachments are still uploading — try again in a moment.');
			return;
		}
		if (isMissingContact && !noContactConfirmed) {
			setNoContactConfirmed(true);
			return;
		}

		const payloadContact = form.manualContact
			? manualEmail
				? { email: manualEmail, name: form.contact?.name?.trim() || undefined }
				: undefined
			: (form.contact ?? undefined);

		createTicket.mutate(
			{
				mode: 'internal',
				subject: trimmedSubject,
				descriptionHtml,
				mailboxId: form.mailboxId,
				priority: form.priority,
				channel,
				categoryId: form.validCategoryId || undefined,
				accountId: form.account?.id,
				contact: payloadContact,
				attachmentIds: attachments.pendingAttachments.map(attachment => attachment.id),
			},
			{
				onSuccess: ({ ticket, warnings }) => {
					form.clearDraft();
					const toastOptions = onCreated
						? undefined
						: {
								action: {
									label: 'View ticket',
									onClick: () => window.open(buildAdminSupportTicketUrl(ticket.id), '_blank'),
								},
							};
					if (warnings.length > 0) {
						toast.warning(
							`Ticket #${ticket.number} was created, but we couldn't ${warnings.join(' or ')}. You can finish that from the ticket view.`,
							toastOptions
						);
					} else {
						toast.success(`Ticket #${ticket.number} created`, toastOptions);
					}
					onCreated?.(ticket);
					onClose();
				},
				onError: err => setError(getApiErrorMessage(err, 'Failed to create the ticket. Please try again.')),
			}
		);
	};

	return (
		<Dialog
			open
			onOpenChange={open => {
				if (!open) onClose();
			}}
		>
			<DialogPopup styles={[s.popup]}>
				<DialogHeader>
					<DialogTitle>New Support Ticket</DialogTitle>
					<DialogDescription>Your draft saves automatically — close and come back anytime.</DialogDescription>
				</DialogHeader>
				<div className={css(shared.modalBody)} onPaste={handlePaste}>
					<NewTicketFields
						form={form}
						contactLabel='Contact'
						contactHint={
							noContactConfirmed &&
							isMissingContact && (
								<span className={css(s.noContactWarning)}>
									No contact selected, so replies won&#8217;t reach anyone. Pick a contact, or click Create Ticket again
									to create it without one.
								</span>
							)
						}
						subjectPlaceholder='Brief summary of the issue'
					>
						<div>
							<Label styles={[shared.modalLabel]}>Description</Label>
							{/* biome-ignore lint/a11y/noStaticElementInteractions: clicking the frame's padding focuses the editor inside it. */}
							<div className={css(s.descriptionEditor)} onClick={() => editor?.commands.focus()}>
								<EditorContent editor={editor} />
							</div>
							<div className={css(s.attachRow)}>
								<Button
									variant='link'
									styles={[s.manualLink]}
									onClick={() => attachments.fileInputRef.current?.click()}
								>
									Attach screenshots or files
								</Button>
								<span className={css(s.draftHint)}>
									Paste or drop works too — images go inline, other files attach.
								</span>
							</div>
							<Input
								ref={attachments.fileInputRef}
								type='file'
								multiple
								styles={[s.fileInput]}
								onChange={handleFileSelect}
							/>
							<ComposerAttachmentList
								attachments={attachments.pendingAttachments}
								onRemove={attachments.removeAttachment}
							/>
							{attachments.uploadError && <div className={css(shared.modalErrorText)}>{attachments.uploadError}</div>}
							{inlineImages.imageUploadError && (
								<div className={css(shared.modalErrorText)}>{inlineImages.imageUploadError}</div>
							)}
						</div>
						<Label htmlFor='new-ticket-phone-call' styles={[s.phoneCallRow]}>
							<Checkbox
								id='new-ticket-phone-call'
								checked={channel === Channel.Phone}
								onCheckedChange={checked => setChannel(checked === true ? Channel.Phone : Channel.Email)}
							/>
							<span>Originated from a phone call</span>
						</Label>
					</NewTicketFields>
					{error && <div className={css(shared.modalErrorText)}>{error}</div>}
				</div>
				<DialogFooter>
					<Button variant='outline' size='sm' onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant='primary'
						size='sm'
						onClick={handleSubmit}
						disabled={createTicket.isPending || form.mailboxesLoading || form.mailboxesFailed || form.hasNoAgentMailbox}
					>
						{createTicket.isPending ? 'Creating...' : 'Create Ticket'}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
